import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import subscriptionClassService from '../../services/subscriptionClassService';


const SubscriptionClasses = ({ subscription, onClose }) => {
  const [classes, setClasses] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [classesMeta, setClassesMeta] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classForm, setClassForm] = useState({
    classe_id: '',
    status: 'active',
    max_students_override: '',
    configuration: {},
    permissions: [],
    restrictions: []
  });

  // Charger les données
  const loadClasses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await subscriptionClassService.getClasses(subscription.slug);
      if (response.success) {
        setClasses(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement classes:', error);
      toast.error(error.message || 'Erreur lors du chargement des classes');
    } finally {
      setLoading(false);
    }
  }, [subscription.slug]);

  const loadAvailableClasses = useCallback(async () => {
    try {
      const response = await subscriptionClassService.getAvailableClasses(subscription.slug);
      if (response.success) {
        setAvailableClasses(response.data);
        // Stocker les métadonnées si elles existent
        if (response.meta) {
          setClassesMeta(response.meta);
        }
      }
    } catch (error) {
      console.error('Erreur chargement classes disponibles:', error);
    }
  }, [subscription.slug]);

  const loadStats = useCallback(async () => {
    try {
      const response = await subscriptionClassService.getClassesStats(subscription.slug);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
  }, [subscription.slug]);

  useEffect(() => {
    loadClasses();
    loadAvailableClasses();
    loadStats();
  }, [loadClasses, loadAvailableClasses, loadStats]);

  // Gestion des actions
  const handleAddClass = async (e) => {
    e.preventDefault();
    
    if (!classForm.classe_id) {
      toast.error('Veuillez sélectionner une classe');
      return;
    }

    // Vérifier les limites
    if (classesMeta.can_add_more === false) {
      toast.error('Vous avez atteint la limite maximum de classes pour ce plan');
      return;
    }

    try {
      const response = await subscriptionClassService.addClass(subscription.slug, classForm);
      if (response.success) {
        toast.success('Classe ajoutée avec succès');
        setShowAddModal(false);
        resetForm();
        loadClasses();
        loadAvailableClasses();
        loadStats();
      }
    } catch (error) {
      console.error('Erreur ajout classe:', error);
      toast.error(error.message || 'Erreur lors de l\'ajout de la classe');
    }
  };

  const handleEditClass = async (e) => {
    e.preventDefault();

    try {
      const response = await subscriptionClassService.updateClass(
        subscription.slug, 
        selectedClass.slug, 
        classForm
      );
      if (response.success) {
        toast.success('Classe mise à jour avec succès');
        setShowEditModal(false);
        resetForm();
        loadClasses();
        loadStats();
      }
    } catch (error) {
      console.error('Erreur mise à jour classe:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteClass = async () => {
    try {
      const response = await subscriptionClassService.removeClass(subscription.slug, selectedClass.slug);
      if (response.success) {
        toast.success('Classe retirée avec succès');
        setShowDeleteModal(false);
        setSelectedClass(null);
        loadClasses();
        loadAvailableClasses();
        loadStats();
      }
    } catch (error) {
      console.error('Erreur suppression classe:', error);
      toast.error(error.message || 'Erreur lors de la suppression');
    }
  };

  const handleSyncStudents = async (classItem) => {
    try {
      const response = await subscriptionClassService.syncStudents(subscription.slug, classItem.slug);
      if (response.success) {
        toast.success('Synchronisation des élèves réussie');
        loadClasses();
        loadStats();
      }
    } catch (error) {
      console.error('Erreur synchronisation:', error);
      toast.error(error.message || 'Erreur lors de la synchronisation');
    }
  };

  const openEditModal = (classItem) => {
    setSelectedClass(classItem);
    setClassForm({
      classe_id: classItem.classe_id,
      status: classItem.status || 'active',
      max_students_override: classItem.max_students_override || '',
      configuration: classItem.configuration || {},
      permissions: classItem.permissions || [],
      restrictions: classItem.restrictions || []
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setClassForm({
      classe_id: '',
      status: 'active',
      max_students_override: '',
      configuration: {},
      permissions: [],
      restrictions: []
    });
    setSelectedClass(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { class: 'bg-success', text: 'Actif' },
      suspended: { class: 'bg-warning', text: 'Suspendu' },
      inactive: { class: 'bg-secondary', text: 'Inactif' }
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-classes">
      {/* En-tête */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Classes - {subscription.name}</h4>
          <p className="text-muted mb-0">Gestion des classes liées à l'abonnement</p>
        </div>
        <div>
          <button 
            className="btn btn-primary me-2"
            onClick={() => setShowAddModal(true)}
            disabled={classesMeta.can_add_more === false}
            title={classesMeta.can_add_more === false ? 'Limite de classes atteinte' : 'Ajouter une classe'}
          >
            <i className="bi bi-plus-lg me-1"></i>
            Ajouter une classe
          </button>
          <button className="btn btn-outline-secondary" onClick={onClose}>
            <i className="bi bi-arrow-left me-1"></i>
            Retour
          </button>
        </div>
      </div>

      {/* Statistiques */}
      {Object.keys(stats).length > 0 && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-primary">{stats.total_classes || 0}</h5>
                <p className="card-text">Classes total</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-success">{stats.active_classes || 0}</h5>
                <p className="card-text">Classes actives</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-info">{stats.total_students || 0}</h5>
                <p className="card-text">Élèves total</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-warning">{stats.average_completion_rate || 0}%</h5>
                <p className="card-text">Taux moyen</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des classes */}
      <div className="card">
        <div className="card-header">
          <h6 className="mb-0">Classes de l'abonnement ({classes.length})</h6>
        </div>
        <div className="card-body">
          {classes.length === 0 ? (
            <div className="text-center py-4">
              <i className="bi bi-collection display-1 text-muted"></i>
              <h5 className="text-muted mt-3">Aucune classe</h5>
              <p className="text-muted">Aucune classe n'est associée à cet abonnement.</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
              >
                Ajouter la première classe
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Classe</th>
                    <th>Statut</th>
                    <th>Élèves actifs</th>
                    <th>Limite élèves</th>
                    <th>Taux completion</th>
                    <th>Dernière activité</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map((classItem) => (
                    <tr key={classItem.slug}>
                      <td>
                        <div>
                          <strong>{classItem.classe?.label || `Classe ${classItem.classe_id}`}</strong>
                          <br />
                          <small className="text-muted">
                            Ajoutée le {formatDate(classItem.added_at)}
                          </small>
                        </div>
                      </td>
                      <td>{getStatusBadge(classItem.status)}</td>
                      <td>
                        <span className="badge bg-info">
                          {classItem.active_students_count || 0}
                        </span>
                      </td>
                      <td>
                        {classItem.max_students_override ? (
                          <span className="text-warning">
                            {classItem.max_students_override} (personnalisé)
                          </span>
                        ) : (
                          <span className="text-muted">
                            {subscription.max_students || 'Illimité'} (plan)
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="progress" style={{height: '20px'}}>
                          <div 
                            className="progress-bar" 
                            style={{width: `${classItem.average_completion_rate || 0}%`}}
                          >
                            {classItem.average_completion_rate || 0}%
                          </div>
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(classItem.last_activity_at)}
                        </small>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button 
                            className="btn btn-outline-primary"
                            onClick={() => openEditModal(classItem)}
                            title="Modifier"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button 
                            className="btn btn-outline-info"
                            onClick={() => handleSyncStudents(classItem)}
                            title="Synchroniser les élèves"
                          >
                            <i className="bi bi-arrow-repeat"></i>
                          </button>
                          <button 
                            className="btn btn-outline-danger"
                            onClick={() => {
                              setSelectedClass(classItem);
                              setShowDeleteModal(true);
                            }}
                            title="Retirer"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Ajouter une classe */}
      {showAddModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ajouter une classe</h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                ></button>
              </div>
              <form onSubmit={handleAddClass}>
                <div className="modal-body">
                  {/* Informations sur les limites */}
                  {Object.keys(classesMeta).length > 0 && (
                    <div className="alert alert-info mb-3">
                      <h6 className="alert-heading">
                        <i className="bi bi-info-circle me-2"></i>
                        Informations sur les limites
                      </h6>
                      <div className="row">
                        <div className="col-md-4">
                          <strong>Classes actuelles:</strong> {classesMeta.current_count || 0}
                        </div>
                        <div className="col-md-4">
                          <strong>Maximum autorisé:</strong> {classesMeta.max_allowed || 'Illimité'}
                        </div>
                        <div className="col-md-4">
                          <strong>Peut ajouter:</strong> 
                          <span className={`ms-1 ${classesMeta.can_add_more ? 'text-success' : 'text-danger'}`}>
                            {classesMeta.can_add_more ? 'Oui' : 'Non'}
                          </span>
                        </div>
                      </div>
                      {!classesMeta.can_add_more && (
                        <div className="mt-2">
                          <small className="text-warning">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            Vous avez atteint la limite maximum de classes pour ce plan.
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="row">
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">Classe à ajouter *</label>
                        <select
                          className="form-select"
                          value={classForm.classe_id}
                          onChange={(e) => setClassForm({...classForm, classe_id: e.target.value})}
                          required
                        >
                          <option value="">Sélectionner une classe...</option>
                          {availableClasses.map((classe) => (
                            <option key={classe.id} value={classe.id}>
                              {classe.label} - {classe.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Statut</label>
                        <select
                          className="form-select"
                          value={classForm.status}
                          onChange={(e) => setClassForm({...classForm, status: e.target.value})}
                        >
                          <option value="active">Actif</option>
                          <option value="suspended">Suspendu</option>
                          <option value="inactive">Inactif</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Limite d'élèves (optionnel)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={classForm.max_students_override}
                          onChange={(e) => setClassForm({...classForm, max_students_override: e.target.value})}
                          placeholder="Laisser vide pour utiliser la limite du plan"
                          min="1"
                        />
                        <small className="text-muted">
                          Limite actuelle du plan: {subscription.max_students || 'Illimitée'}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={classesMeta.can_add_more === false}
                  >
                    Ajouter la classe
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier une classe */}
      {showEditModal && selectedClass && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Modifier la classe - {selectedClass.class_name || `Classe ${selectedClass.classe_id}`}
                </h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                ></button>
              </div>
              <form onSubmit={handleEditClass}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Statut</label>
                        <select
                          className="form-select"
                          value={classForm.status}
                          onChange={(e) => setClassForm({...classForm, status: e.target.value})}
                        >
                          <option value="active">Actif</option>
                          <option value="suspended">Suspendu</option>
                          <option value="inactive">Inactif</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Limite d'élèves personnalisée</label>
                        <input
                          type="number"
                          className="form-control"
                          value={classForm.max_students_override}
                          onChange={(e) => setClassForm({...classForm, max_students_override: e.target.value})}
                          placeholder="Laisser vide pour utiliser la limite du plan"
                          min="1"
                        />
                        <small className="text-muted">
                          Limite du plan: {subscription.max_students || 'Illimitée'}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Informations de la classe */}
                  <div className="alert alert-info">
                    <h6>Informations de la classe</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <strong>Élèves actifs:</strong> {selectedClass.active_students_count || 0}
                      </div>
                      <div className="col-md-6">
                        <strong>Leçons terminées:</strong> {selectedClass.completed_lessons || 0}/{selectedClass.total_lessons || 0}
                      </div>
                      <div className="col-md-6">
                        <strong>Ajoutée le:</strong> {formatDate(selectedClass.added_at)}
                      </div>
                      <div className="col-md-6">
                        <strong>Dernière activité:</strong> {formatDate(selectedClass.last_activity_at)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      resetForm();
                    }}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Mettre à jour
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation suppression */}
      {showDeleteModal && selectedClass && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmer la suppression</h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedClass(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Êtes-vous sûr de vouloir retirer la classe 
                  <strong> {selectedClass.class_name || `Classe ${selectedClass.classe_id}`} </strong>
                  de cet abonnement ?
                </p>
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Cette action retirera la classe de l'abonnement mais ne supprimera pas la classe elle-même.
                  Les élèves de cette classe perdront l'accès aux fonctionnalités de l'abonnement.
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedClass(null);
                  }}
                >
                  Annuler
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteClass}
                >
                  Retirer la classe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionClasses;
